/**
 * Merchant Subscription Plan page — iteration 35k.
 * Free / Standard / Pro chooser aligned with the new tiered commission logic:
 *   Free     — 3 campaigns · 4.99% commission (capped at $30)
 *   Standard — unlimited   · 3.99% commission (capped at $30) + advanced analytics
 *   Pro      — unlimited   · 3.49% commission (capped at $30) + Insights + priority
 *
 * "Standard" upgrades to the existing `growth` Stripe SKU; "Pro" upgrades to `scale`.
 * Alias is transparent to the merchant — they see Free/Standard/Pro end-to-end.
 *
 * Loop P2 · When `FEATURE_BILLING_STARTER_GROWTH=true` on the backend, this
 * page delegates to `StarterGrowthBilling`. Flag is off by default so the
 * legacy UI below stays live for every merchant until you flip it.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Check, Sparkles, Crown, CreditCard, ArrowRight, Rocket,
  BarChart3, Trophy, Zap, Headphones, Handshake, Percent,
} from "lucide-react";
import StarterGrowthBilling from "@/pages/merchant/StarterGrowthBilling";
import BusinessTypeRequiredGate from "@/components/merchant/BusinessTypeRequiredGate";
import PerformanceFeesCard from "@/components/merchant/PerformanceFeesCard";
import UsdSettlementsCard from "@/components/merchant/UsdSettlementsCard";

// UI-plan → Stripe SKU per billing cycle. All keys exist in backend TIER_PACKAGES.
const SKU_LOOKUP = {
  standard: { monthly: "standard_monthly", quarterly: "standard_quarterly", annual: "standard_annual" },
  pro:      { monthly: "pro_monthly",      quarterly: "pro_quarterly",      annual: "pro_annual"      },
};

const PLAN_META = [
  {
    id: "free",
    stored_tier: "starter",
    label: "Free",
    icon: Sparkles,
    tagline: "Get started, launch your first offers",
    monthly: 0,
    cta: "Included",
    commission: "5.99%",
    features: [
      { text: "Up to 3 active campaigns",        included: true },
      { text: "Auto-generated unique coupon codes", included: true },
      { text: "Basic conversion stats — clicks, conversions, revenue", included: true },
      { text: "Fee cap of $49.99 per transaction",  included: true },
      { text: "Advanced analytics — top offers, geo, funnel", included: false },
      { text: "Insights leaderboard",             included: false },
      { text: "Priority support",                 included: false },
    ],
  },
  {
    id: "standard",
    stored_tier: "growth",
    label: "Growth",
    icon: Rocket,
    tagline: "For growing brands ready to scale",
    monthly: 69,
    quarterly_monthly: 64,   // $192/quarter · save $5/mo
    annual_monthly: 59,   // $708/yr vs $828/yr monthly → ~14.5% saved
    cta: "Upgrade to Growth",
    commission: "3.99%",
    recommended: true,
    features: [
      { text: "Unlimited active campaigns",        included: true },
      { text: "Auto-generated unique coupon codes", included: true },
      { text: "Basic conversion stats",            included: true },
      { text: "Fee cap of $30 per transaction",   included: true },
      { text: "Advanced analytics — top offers, geo, funnel", included: true },
      { text: "Insights leaderboard",              included: false },
      { text: "Priority support",                  included: false },
    ],
  },
  {
    id: "pro",
    stored_tier: "scale",
    label: "Pro",
    icon: Crown,
    tagline: "Full-power performance commerce",
    monthly: 99,
    quarterly_monthly: 89,
    annual_monthly: 79,
    cta: "Upgrade to Pro",
    commission: "3.49%",
    features: [
      { text: "Unlimited active campaigns",        included: true },
      { text: "Auto-generated unique coupon codes", included: true },
      { text: "Basic conversion stats",            included: true },
      { text: "Fee cap of $30 per transaction",   included: true },
      { text: "Advanced analytics — top offers, geo, funnel", included: true },
      { text: "Insights leaderboard + category benchmarks", included: true },
      { text: "Priority support",                  included: true },
    ],
  },
];

export default function MerchantBilling() {
  // Loop P2 · If the new Starter/Growth surface is enabled on the backend,
  // hand off entirely. Otherwise fall through to the legacy UI below.
  const [sgEnabled, setSgEnabled] = useState(null);   // null = probing, true/false = decided
  useEffect(() => {
    let cancelled = false;
    api.get("/billing/sg/plans")
      .then(() => { if (!cancelled) setSgEnabled(true); })
      .catch(() => { if (!cancelled) setSgEnabled(false); });
    return () => { cancelled = true; };
  }, []);
  if (sgEnabled === null) return null;               // one-frame probe, no flash
  // SAAS-USD-BILLING-1 · Phase 0.5 — gate ALL billing surfaces on the
  // merchant having chosen a Business Type. This is UI-only; backend
  // billing endpoints remain the source of truth and are unchanged.
  const Body = sgEnabled ? StarterGrowthBilling : LegacyMerchantBilling;
  return (
    <BusinessTypeRequiredGate>
      <Body />
      {/* SAAS-USD-BILLING-1 · Phase 3 — always render, both cards
          self-gate internally when the merchant has no data. */}
      <PerformanceFeesCard />
      <UsdSettlementsCard />
    </BusinessTypeRequiredGate>
  );
}

function LegacyMerchantBilling() {
  const [me, setMe] = useState(null);
  const [busyPlan, setBusyPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly"); // "monthly" | "quarterly" | "annual"
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");

  const loadMe = async () => {
    try {
      const { data } = await api.get("/billing/me");
      setMe(data);
    } catch {
      setMe(null);
    }
  };
  useEffect(() => { loadMe(); }, []);

  // Post-checkout return polling — flips the UI when Stripe reports paid.
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid" && data.outcome?.processed) {
          toast.success(`Welcome to the ${(data.tier || "").toUpperCase()} plan!`);
          await loadMe();
          params.delete("session_id"); setParams(params, { replace: true });
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout expired. Please try again.");
          params.delete("session_id"); setParams(params, { replace: true });
          return;
        }
        if (attempts < 8) setTimeout(tick, 2000);
      } catch { if (attempts < 6) setTimeout(tick, 2000); }
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (cancelled) toast.info("Checkout cancelled — no changes to your plan.");
  }, [cancelled]);

  const currentPlan = (() => {
    const tier = (me?.tier || "starter").toLowerCase();
    // Alias legacy tier names to the UI plan ids.
    if (tier === "starter" || tier === "free") return "free";
    if (tier === "growth" || tier === "standard") return "standard";
    if (tier === "scale" || tier === "pro") return "pro";
    return "free";
  })();

  const upgrade = async (planId) => {
    if (planId === "free") return;
    const sku = SKU_LOOKUP[planId]?.[billingCycle];
    if (!sku) return;
    setBusyPlan(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        tier: sku, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusyPlan(null);
    }
  };

  const isPendingKyb = !!(me && me.store_status && me.store_status !== "approved");

  return (
    <div data-testid="merchant-billing">
      <div className="pf-chip pf-chip-cyan"><CreditCard className="w-3 h-3" /> Subscription</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">
        Choose your <span className="pf-gradient-text-2">Cyannect plan</span>
      </h2>
      {isPendingKyb && (
        <div className="mt-4 rounded-2xl border border-cyan-200 bg-cyan-50 p-4 text-sm text-cyan-900" data-testid="billing-verification-notice">
          <div className="font-semibold">Subscribe now — verification still runs in parallel</div>
          <div className="mt-1 text-cyan-800">
            Pick a plan and start billing right away. Cyannect KYB
            verification is still needed before any offer you draft can go
            live to shoppers — we'll ping you the moment it clears.
          </div>
        </div>
      )}
      <p className="text-sm text-slate-600 mt-2 max-w-2xl leading-relaxed">
        Growth & Pro subscribers pay a lower commission and enjoy a tighter <strong>CA$30 per-transaction cap</strong>. Free stays at 5.99% capped at CA$49.99 — perfect for testing the waters.
        {me && (
          <span className="ml-1">
            Current plan: <span className="pf-chip pf-chip-mint uppercase font-semibold" data-testid="current-plan-pill">{currentPlan}</span>
            {me.subscription_ends_at && (
              <span className="ml-2 text-xs text-slate-500">
                renews {new Date(me.subscription_ends_at).toLocaleDateString()}
              </span>
            )}
          </span>
        )}
      </p>

      {/* Billing cycle toggle — quarterly & annual save vs monthly on paid plans. */}
      <div className="mt-6 inline-flex items-center gap-1 p-1 rounded-full bg-white/60 ring-1 ring-white/70 shadow-sm" data-testid="billing-cycle-toggle">
        <button
          onClick={() => setBillingCycle("monthly")}
          className={`px-4 py-1.5 rounded-full text-xs font-semibold transition ${billingCycle === "monthly" ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:text-slate-900"}`}
          data-testid="cycle-monthly"
        >
          Monthly
        </button>
        <button
          onClick={() => setBillingCycle("quarterly")}
          className={`px-4 py-1.5 rounded-full text-xs font-semibold transition inline-flex items-center gap-1.5 ${billingCycle === "quarterly" ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:text-slate-900"}`}
          data-testid="cycle-quarterly"
        >
          Quarterly
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${billingCycle === "quarterly" ? "bg-cyan-400/90 text-cyan-950" : "bg-cyan-100 text-cyan-700"}`}>
            Save up to $10
          </span>
        </button>
        <button
          onClick={() => setBillingCycle("annual")}
          className={`px-4 py-1.5 rounded-full text-xs font-semibold transition inline-flex items-center gap-1.5 ${billingCycle === "annual" ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:text-slate-900"}`}
          data-testid="cycle-annual"
        >
          Annual
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${billingCycle === "annual" ? "bg-emerald-400/90 text-emerald-950" : "bg-emerald-100 text-emerald-700"}`}>
            Save up to 20%
          </span>
        </button>
      </div>
      <div className="text-[11px] text-slate-500 mt-2" data-testid="billing-cycle-hint">
        {billingCycle === "annual"
          ? "Billed annually · commitment locks in the discount."
          : billingCycle === "quarterly"
            ? "Billed every 3 months · lower rate, shorter commitment."
            : "No commitment · pay month-to-month."}
      </div>

      {/* Plan cards */}
      <div className="grid md:grid-cols-3 gap-4 mt-8">
        {PLAN_META.map((plan) => {
          const Icon = plan.icon;
          const isCurrent = currentPlan === plan.id;
          const busy = busyPlan === plan.id;
          return (
            <div
              key={plan.id}
              className={`pf-card p-7 flex flex-col relative overflow-hidden ${
                plan.recommended ? "ring-2 ring-cyan-400/60" : ""
              } ${isCurrent ? "ring-2 ring-emerald-400/60" : ""}`}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.recommended && (
                <span className="absolute top-4 right-4 pf-chip pf-chip-lav text-[10px] font-semibold">Recommended</span>
              )}
              {isCurrent && !plan.recommended && (
                <span className="absolute top-4 right-4 pf-chip pf-chip-mint text-[10px] font-semibold">Current</span>
              )}
              {isCurrent && plan.recommended && (
                <span className="absolute top-14 right-4 pf-chip pf-chip-mint text-[10px] font-semibold">Current</span>
              )}
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-cyan-600" />
                <div className="font-display text-xl">{plan.label}</div>
              </div>
              <p className="text-xs text-slate-500 mt-1">{plan.tagline}</p>
              <div className="mt-4">
                {(() => {
                  const isQuarterly = billingCycle === "quarterly" && plan.quarterly_monthly != null;
                  const isAnnual = billingCycle === "annual" && plan.annual_monthly != null;
                  const displayPrice = isAnnual ? plan.annual_monthly : isQuarterly ? plan.quarterly_monthly : plan.monthly;
                  const strike = (isAnnual || isQuarterly) && plan.monthly > 0 ? plan.monthly : null;
                  return (
                    <>
                      {strike != null && (
                        <span className="mr-2 text-sm text-slate-400 line-through tabular-nums" data-testid={`plan-price-strike-${plan.id}`}>CA${strike}</span>
                      )}
                      <span className="font-display text-4xl font-medium tracking-tight tabular-nums" data-testid={`plan-price-${plan.id}`}>
                        ${displayPrice}
                      </span>
                      <span className="text-slate-500 text-sm ml-1">/month</span>
                      {isAnnual && plan.monthly > 0 && (
                        <div className="text-[11px] text-emerald-700 font-semibold mt-1" data-testid={`plan-annual-savings-${plan.id}`}>
                          Billed annually · ${displayPrice * 12}/year (save ${((plan.monthly - plan.annual_monthly) * 12)})
                        </div>
                      )}
                      {isQuarterly && plan.monthly > 0 && (
                        <div className="text-[11px] text-cyan-700 font-semibold mt-1" data-testid={`plan-quarterly-savings-${plan.id}`}>
                          Billed quarterly · ${displayPrice * 3}/quarter (save ${(plan.monthly - plan.quarterly_monthly) * 3}/quarter)
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-100 px-3 py-1"
                   data-testid={`plan-commission-${plan.id}`}>
                <Percent className="w-3 h-3 text-emerald-700" />
                <span className="text-xs font-semibold text-emerald-800">{plan.commission}</span>
                <span className="text-[11px] text-slate-500">commission per sale</span>
              </div>
              <ul className="space-y-2 mt-6 flex-1">
                {plan.features.map((f) => (
                  <li key={f.text} className={`text-sm flex items-start gap-2 ${f.included ? "text-slate-700" : "text-slate-400 line-through decoration-slate-300"}`}>
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${f.included ? "text-emerald-600" : "text-slate-300"}`} />
                    <span>{f.text}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => upgrade(plan.id)}
                disabled={plan.id === "free" || isCurrent || busy}
                className={`mt-6 py-2.5 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-1.5 ${
                  plan.recommended ? "pf-btn-primary" : "pf-btn-glass"
                } disabled:opacity-60 disabled:cursor-not-allowed`}
                data-testid={`plan-cta-${plan.id}`}
              >
                {isCurrent ? "Current plan" : busy ? "Redirecting…" : plan.cta}
                {!isCurrent && plan.id !== "free" && !busy && <ArrowRight className="w-3.5 h-3.5" />}
              </button>
            </div>
          );
        })}
      </div>

      {/* Feature comparison strip */}
      <div className="mt-8 grid md:grid-cols-4 gap-3" data-testid="plan-highlights">
        {[
          { icon: Zap, title: "Unlimited campaigns", body: "From Growth tier up — no more hitting a cap when you launch a new drop." },
          { icon: BarChart3, title: "Advanced analytics", body: "Growth + Pro unlock top-offer breakdowns, geo, funnel, and category benchmarks." },
          { icon: Trophy, title: "Insights leaderboard", body: "Pro-only. See where you rank vs peer merchants in your category — no names shown." },
          { icon: Headphones, title: "Priority support", body: "Pro subscribers jump the queue with dedicated success responses." },
        ].map((h) => (
          <div key={h.title} className="pf-card p-4">
            <h.icon className="w-4 h-4 text-cyan-600" />
            <div className="text-sm font-semibold text-slate-900 mt-2">{h.title}</div>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{h.body}</p>
          </div>
        ))}
      </div>

      {/* Payment history — kept from the previous Billing page */}
      <VisitPacks meRefresh={loadMe} />
      {me?.history && me.history.length > 0 && (
        <div className="pf-card overflow-hidden mt-8" data-testid="payment-history">
          <div className="p-4 text-xs uppercase tracking-widest text-slate-500 font-semibold">Payment history</div>
          <table className="w-full text-sm">
            <thead className="bg-white/40 text-left text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-2">Date</th>
                <th className="px-5 py-2">Plan</th>
                <th className="px-5 py-2">Amount</th>
                <th className="px-5 py-2">Status</th>
                <th className="px-5 py-2">Session</th>
              </tr>
            </thead>
            <tbody>
              {me.history.map((t) => (
                <tr key={t.id} className="border-t border-white/60" data-testid={`billing-tx-${t.id}`}>
                  <td className="px-5 py-2 text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="px-5 py-2">{t.tier}</td>
                  <td className="px-5 py-2 tabular-nums">CA${t.amount.toFixed(2)}</td>
                  <td className="px-5 py-2"><span className={`pf-chip ${t.status === "completed" ? "pf-chip-mint" : ""}`}>{t.status}</span></td>
                  <td className="px-5 py-2 font-mono text-[11px] text-slate-500 truncate max-w-[240px]">{t.session_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-8 flex items-start gap-2 text-xs text-slate-500 max-w-2xl">
        <Handshake className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
        <p>
          Cyannect commissions are charged only on <strong>verified conversions</strong> — not on views or clicks. Growth and Pro plans include a tighter {"$30"}-per-transaction cap; Free stays at {"$49.99"}.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visit Packs (iter 38 · Phase B) — add-on subscription for guaranteed monthly
// website visits. Fetched from /api/billing/plans so the copy never drifts.
// ---------------------------------------------------------------------------
function VisitPacks({ meRefresh }) {
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/billing/plans").then((r) => {
      const t = r.data?.addons?.visits?.tiers || [];
      setTiers(t);
    }).catch(() => {});
  }, []);

  const buy = async (skuId) => {
    setBusy(skuId);
    try {
      const { data } = await api.post("/billing/checkout", {
        tier: skuId, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  if (!tiers.length) return null;

  return (
    <section className="mt-12" data-testid="visit-packs">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h3 className="font-display text-2xl font-medium tracking-tight text-slate-900">Website Visits Pack</h3>
        <span className="text-xs text-slate-500">Optional add-on · stacks on any plan</span>
      </div>
      <p className="text-sm text-slate-600 mt-1 max-w-2xl leading-relaxed">
        Buy a monthly quota of billable Cyannect referral clicks. Overage clicks still redirect real shoppers — they just don't count against your pack.
      </p>
      <div className="grid gap-4 md:grid-cols-3 mt-5">
        {tiers.map((t) => {
          const visits = t.id === "visits_pack_1k"  ? 1000
                       : t.id === "visits_pack_10k" ? 10000
                       : t.id === "visits_pack_50k" ? 50000 : null;
          return (
            <div key={t.id} className="pf-card p-5 ring-1 ring-white/60" data-testid={`visits-tier-${t.id}`}>
              <div className="inline-flex items-center gap-1.5 pf-chip pf-chip-cyan">
                <Zap className="w-3 h-3" /> Add-on
              </div>
              <div className="mt-3 text-slate-900">
                <span className="font-display text-3xl font-medium tabular-nums">{visits?.toLocaleString()}</span>
                <span className="text-slate-500 text-sm ml-1">visits/mo</span>
              </div>
              <div className="mt-1 text-slate-900">
                <span className="font-display text-2xl font-medium tabular-nums">CA${t.monthly}</span>
                <span className="text-slate-500 text-sm ml-1">/month</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-2">
                ≈ ${((t.monthly / visits) * 1000).toFixed(2)} per 1k visits
              </div>
              <button
                onClick={() => buy(t.id)}
                disabled={!!busy}
                className="mt-5 w-full py-2.5 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-1.5 pf-btn-glass disabled:opacity-60"
                data-testid={`visits-tier-cta-${t.id}`}
              >
                {busy === t.id ? "Redirecting…" : "Add to plan"} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
